<script setup lang="ts">
import { PageWrapperComponent } from '@metapic-apps/shared'
import { computed } from 'vue'

import TestOrderBox from './TestOrderBox.vue'

import { useGetSalesPixelSettings } from '@/api/advertiser.api'
import ArrowIcon from '@/assets/icons/arrow.svg?component'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const advertiserStore = useAdvertiserStore()
const advertiser = computed(() => advertiserStore?.advertiser)

const { data } = useGetSalesPixelSettings(advertiser)

const params = [
  {
    name: 'ORDER_NUMBER',
    description: `A unique id that should be generated on your end - you need to replace this placeholder text by an actual tag or variable that will
          automatically inject the respective value on each conversion. It can be anything that is meaningful to you, and unique for each
          transaction: an order number, user id, email address, etc. After a conversion has taken place, you can find this id alongside the
          conversion on our platform. This makes it very easy to cross-reference data on our platform with your own administration.`,
  },
  {
    name: 'ORDER_VALUE',
    description: `The total value of the conversion.
      We use this value to calculate any percentage-based commissions you might have set up for your program.
      Be aware you also need to replace the placeholder text in the code by the actual tag or variable that passes this data on your website,
      dinamically injecting a new value on each transaction`,
  },
  {
    name: 'VOUCHER_CODE',
    description: `the voucher or coupon code that a customer uses during checkout.
    You need to replace this placeholder text with the actual code, dynamically inserted for each order
    (if the code is FREEDEL for free delivery then we need to receive FREEDEL instead of this placeholder).`,
  },
]

const getLandingTrackingCode = computed(() => {
  const programID = data.value?.programId
  const organizationId = data.value?.organizationId
  return `&lt;!-- Start Metapic Landing Page Tag Insert on all landing pages to handle first party cookie--&gt;
&lt;script language=&quot;JavaScript&quot;&gt;
(function(i,s,o,g,r,a,m){i[&#39;TDConversionObject&#39;]=r;i[r]=i[r]||function(){(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)})(window,document,&#39;script&#39;, &#39;https://svht.tradedoubler.com/tr_sdk.js?org=${organizationId}&amp;prog=${programID}&amp;dr=true&amp;rand=&#39; + Math.random(), &#39;tdconv&#39;);
&lt;/script&gt;
&lt;!-- End Metapic tag--&gt;`
})

const getConfirmationTrackingCode = computed(() => {
  const organizationId = data.value?.organizationId
  const currency = data.value?.currency
  const eventId = data.value?.eventId

  return `&lt;!-- Start Metapic Conversion Tag Insert on confirmation Page --&gt;
&lt;script language=&quot;JavaScript&quot;&gt;
tdconv(&#39;init&#39;, &#39;${organizationId}&#39;, {&#39;element&#39;: &#39;iframe&#39; });
tdconv(&#39;track&#39;, &#39;sale&#39;, {&#39;transactionId&#39;:&#39;<span class="highlight">[ORDER_NUMBER]</span>&#39;, &#39;ordervalue&#39;:<span class="highlight">[ORDER_VALUE]</span>, &#39;voucher&#39;:&#39;<span class="highlight">[VOUCHER_CODE]</span>&#39;, &#39;currency&#39;:&#39;${currency}&#39;, &#39;event&#39;:${eventId}});
&lt;/script&gt;
&lt;!-- End Metapic tag--&gt;`
})
</script>

<template>
  <PageWrapperComponent>
    <BoxComponent :max-w="856">
      <BoxComponent :mt="32" :mb="48">
        <FlexComponent :gap="16" align="center">
          <router-link to="/orders" class="back-link">
            <el-icon class="arrow-icon" size="24">
              <ArrowIcon />
            </el-icon>
          </router-link>
          <TypographyComponent variant="headline-large" color="var(--primary)">Connect your sales pixel</TypographyComponent>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent :mb="24">
        <TypographyComponent variant="headline-medium">Installation of JavaScript pixel</TypographyComponent>
      </BoxComponent>
      <div v-if="data" class="collection-code-step">
        <BoxComponent :mb="24">
          <TypographyComponent variant="headline-small">Insert code on all landing pages</TypographyComponent>
        </BoxComponent>
        <TypographyComponent variant="body-large"
          >To handle first party cookie, please insert this code snippet on all landing pages.</TypographyComponent
        >
        <!-- eslint-disable-next-line -->
        <code v-html="getLandingTrackingCode" class="javascript-snippet"></code>
        <BoxComponent :mb="24">
          <TypographyComponent variant="headline-small">Insert code on confirmation page</TypographyComponent>
        </BoxComponent>
        <TypographyComponent variant="body-large">Please insert this code snippet on your order confirmation page.</TypographyComponent>
        <!-- eslint-disable-next-line -->
        <code v-html="getConfirmationTrackingCode" class="javascript-snippet"></code>
      </div>
      <LoadingComponent v-else />
      <dl class="instruction-list">
        <template v-for="p in params" :key="p.name">
          <dt>
            <TypographyComponent variant="title-large" font-weight="bold">[{{ p.name }}]</TypographyComponent>
          </dt>
          <dd>{{ p.description }}</dd>
        </template>
      </dl>
      <TestOrderBox
        v-if="data"
        :latest-order-at="data.latestOrderAt"
        :test-order-url="data.testOrderUrl"
        :latest-order-value="data.latestOrderValue"
      />
    </BoxComponent>
  </PageWrapperComponent>
</template>

<style lang="scss" scoped>
.javascript-snippet {
  display: block;
  padding: 10px;
  color: black;
  background: #dddfe470;
  margin: 10px 0;
}

.arrow-icon {
  transform: rotate(180deg);
}

.instruction-list {
  margin-top: 20px;
}
</style>
